import { PointerSensor, useSensor, useSensors, type DragCancelEvent, type DragEndEvent, type DragOverEvent, type DragStartEvent } from '@dnd-kit/core';
import type { EagGridStore } from '../store/createEagGridStore';
import type { EagColumnState } from '../types/EagColumnState';
import { useColumnDrag } from './useColumnDrag';
const DRAG_ACTIVATION_DISTANCE = 8;
type UseGridDragAndDropParams = {
  visibleColumnStates: EagColumnState[];
  store: EagGridStore;
  dragManager: ReturnType<typeof useColumnDrag>;
};
type UseGridDragAndDropReturn = {
  sensors: ReturnType<typeof useSensors>;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: (event: DragCancelEvent) => void;
};
export const useGridDragAndDrop = (params: UseGridDragAndDropParams): UseGridDragAndDropReturn => {
  // DndContext is owned in EagGridTableInner (not in HeaderRow) so it renders outside <table>.
  // Placing DndContext inside <table> causes dnd-kit to inject accessibility <div>s
  // as direct children of <table>, which is invalid HTML.
  //
  // Drag state lives in EagGridStore so leaf components (HeaderCell, GridCell,
  // SkeletonRow) can read it directly without prop drilling through 4 layers.

  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: DRAG_ACTIVATION_DISTANCE
    }
  }));
  const handleDragStart = (event: DragStartEvent) => {
    params.store.setActiveDragId(String(event.active.id));
  };
  const handleDragOver = (event: DragOverEvent) => {
    const overId = event.over ? String(event.over.id) : null;
    if (overId == null) {
      params.store.setDragOver(null, null);
      return;
    }
    const activeIndex = params.visibleColumnStates.findIndex(cs => cs.key === String(event.active.id));
    const overIndex = params.visibleColumnStates.findIndex(cs => cs.key === overId);
    const side = activeIndex > overIndex ? 'left' : 'right';
    params.store.setDragOver(overId, side);
  };
  const handleDragEnd = (event: DragEndEvent) => {
    params.store.clearDragState();
    params.dragManager.handleDragEnd(event);
  };
  const handleDragCancel = (_event: DragCancelEvent) => {
    params.store.clearDragState();
  };
  return {
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel
  };
};